import {
  CSRF_HEADER,
  type DocumentList,
  type DocumentStatus,
  openModeOf,
  type ProjectDocument,
  type ProjectView,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BookOpenText,
  Download,
  Ellipsis,
  ExternalLink,
  KeyRound,
  RotateCw,
  Trash2,
} from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { api } from "../api.ts";
import { apiError, ok } from "../api-errors.ts";
import { formatBytes, formatDate } from "../i18n/format.ts";
import { t as translate } from "../i18n/i18n.ts";
import { useSession } from "../session.tsx";
import { AudienceLine } from "../trust/AudienceLine.tsx";
import { fileAudience } from "../trust/audience.ts";
import { GeneratedMark, ProvenanceLine } from "../trust/GeneratedMark.tsx";
import { can, canChange } from "./capabilities.ts";
import { DeleteFileDialog } from "./DeleteFileDialog.tsx";
import { downloadHref, openHref } from "./download.ts";
import { EditAccessDialog } from "./EditAccessDialog.tsx";
import { type FileAction, fileActions, isGeneratedDocument, isPaused } from "./file-actions.ts";
import {
  type KnowledgeSettings,
  projectKeys,
  useKnowledgeSettings,
  useMembers,
  usePrincipals,
} from "./queries.ts";
import { useSourceViewer } from "./SourceViewer.tsx";
import { deleteDocumentRequest } from "./upload.ts";

const STATUS_STYLE: Record<DocumentStatus, { dot: string; text: string }> = {
  uploading: { dot: "bg-line-strong", text: "text-fg-secondary" },
  processing: { dot: "bg-warning-border motion-safe:animate-pulse", text: "text-warning-fg" },
  ready: { dot: "bg-success-border", text: "text-success-fg" },
  failed: { dot: "bg-danger-border", text: "text-danger-fg" },
};

/** Catalogue copy for failures, including reasons from a newer server. */
export function failureText(reason: string, ocrMaxPages: number | null): string {
  if (reason === "ocr_page_cap") {
    return ocrMaxPages
      ? translate("files.failure.ocr_page_cap_limit", { limit: ocrMaxPages })
      : translate("files.failure.ocr_page_cap");
  }
  if (reason === "index_drift") {
    return translate("files.failure.unavailable");
  }
  return translate(`files.failure.${reason}`, { defaultValue: translate("files.failure.unknown") });
}

/**
 * What a guest's file list works with: the server keeps the project's settings from guests, so
 * the classification scheme comes with the document list (downloads follow it) and nothing else.
 */
export const OUTSIDER_SETTINGS: KnowledgeSettings = {
  scheme: [],
  defaultClassification: "",
  maxUploadMb: 1,
  ocrMaxPages: null,
};

/**
 * The knowledge settings file rows and uploads work with: the project's for insiders; for guests,
 * who may not read them, the scheme that comes with the document list.
 */
export function useFileSettings(
  project: ProjectView,
  list: DocumentList | undefined,
  enabled = true,
): { data: KnowledgeSettings | undefined; isError: boolean; error: Error | null } {
  // Guests may not read the project's settings or members: their list shows without them.
  const insider = can(project, "project.view_members");
  const knowledge = useKnowledgeSettings(project.id, insider && enabled);
  return insider
    ? knowledge
    : {
        data: { ...OUTSIDER_SETTINGS, scheme: list?.classifications ?? [] },
        isError: false,
        error: null,
      };
}

/**
 * A row's menu driven by a tree (design §4): opened from the row's keyboard (Shift+F10) as well
 * as its button, which is then no tab stop; `extra` adds items (Move to…); focus returns to
 * the row (`returnFocus`) after the menu or a dialog opened from it.
 */
export type RowMenu = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extra?: ReactNode;
  returnFocus: () => HTMLElement | null;
};

/** What a file row shows besides the document: its level's label, audience and actions. */
export type FileRowFacts = {
  classification: string;
  audience: ReturnType<typeof fileAudience>;
  actions: ReactNode;
};

/**
 * The state behind file rows' actions (retry, removal of an incomplete upload, edit access,
 * delete), shared by the Files tab and the knowledge panel. `facts(d)` gives a row what it
 * shows; `overlays` (the last action error and the dialogs) renders once under the list.
 */
export function useFileRows(project: ProjectView, settings: KnowledgeSettings) {
  const { t } = useTranslation();
  const { csrfToken, userId } = useSession();
  const queryClient = useQueryClient();
  // Only for the edit-access dialog: fetched once someone may open it.
  const mayEditAccess =
    canChange(project, "file.manage_any") || canChange(project, "file.manage_own");
  const members = useMembers(project.id, mayEditAccess);
  const principals = usePrincipals(mayEditAccess);
  const [deleting, setDeleting] = useState<ProjectDocument | null>(null);
  const [editing, setEditing] = useState<ProjectDocument | null>(null);
  // The actions button a dialog was opened from: its menu item is gone once the dialog closes.
  const opener = useRef<HTMLElement | null>(null);
  // The last retry or removal failure, whichever came last, naming its file.
  const [actionError, setActionError] = useState<string | null>(null);
  // Documents with a retry or removal in flight (a mutation's `variables` only track the last).
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const mark = (id: string, on: boolean) =>
    setBusy((current) => {
      const next = new Set(current);
      if (on) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  const settle = (d: ProjectDocument | undefined) => {
    if (d) {
      mark(d.id, false);
    }
    return queryClient.invalidateQueries({ queryKey: projectKeys.documents(project.id) });
  };
  const failed = (d: ProjectDocument, error: Error) =>
    setActionError(t("files.actionFailed", { title: d.title, reason: error.message }));
  const retry = useMutation({
    mutationFn: async (d: ProjectDocument) =>
      ok(
        await api.api.projects[":projectId"].documents[":documentId"].retry.$post(
          { param: { projectId: project.id, documentId: d.id } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onMutate: (d) => {
      setActionError(null);
      mark(d.id, true);
    },
    onError: (error, d) => failed(d, error),
    onSettled: (_data, _error, d) => settle(d),
  });
  // Only incomplete uploads: they hold no content yet, so removing them needs no confirmation.
  const remove = useMutation({
    mutationFn: async (d: ProjectDocument) => {
      const res = await deleteDocumentRequest(project.id, d.id, csrfToken);
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onMutate: (d) => {
      setActionError(null);
      mark(d.id, true);
    },
    onError: (error, d) => failed(d, error),
    onSettled: (_data, _error, d) => settle(d),
  });
  const facts = (d: ProjectDocument, menu?: RowMenu): FileRowFacts => {
    const level = settings.scheme.find((l) => l.id === d.classification);
    return {
      classification: level?.label ?? d.classification,
      audience: fileAudience(d.readers, project, userId),
      actions: (
        <Actions
          projectId={project.id}
          document={d}
          actions={fileActions(d, project, userId, level)}
          retrying={d.status === "failed" && busy.has(d.id)}
          removing={d.status === "uploading" && busy.has(d.id)}
          onRetry={() => retry.mutate(d)}
          onRemove={() => remove.mutate(d)}
          onEditAccess={(from) => {
            opener.current = menu?.returnFocus() ?? from;
            setEditing(d);
          }}
          onDelete={(from) => {
            opener.current = menu?.returnFocus() ?? from;
            setDeleting(d);
          }}
          menu={menu}
        />
      ),
    };
  };
  const overlays = (
    <>
      {actionError ? <InlineError>{actionError}</InlineError> : null}
      <DeleteFileDialog
        projectId={project.id}
        document={deleting}
        onClose={() => setDeleting(null)}
        returnFocus={opener.current}
      />
      <EditAccessDialog
        project={project}
        document={editing}
        settings={settings}
        members={members.data}
        principals={principals.data}
        onClose={() => setEditing(null)}
        returnFocus={opener.current}
      />
    </>
  );
  return { facts, overlays };
}

/**
 * Where a generated file came from: the provenance line of an added artefact, or the plain
 * marker for an answer saved from generated sources. Nothing for any other file.
 */
function GeneratedFacts({ document: d }: { document: ProjectDocument }) {
  if (d.provenance) {
    return <ProvenanceLine provenance={d.provenance} />;
  }
  return isGeneratedDocument(d) ? (
    <span className="flex text-xs text-fg-muted">
      <GeneratedMark />
    </span>
  ) : null;
}

/** The metadata words: the origin is said by the generated marker when there is one. */
const originWord = (d: ProjectDocument) =>
  isGeneratedDocument(d) ? null : translate(`files.originLabel.${d.origin}`);

/**
 * One file as a stacked entry (the knowledge tree's item rows), all facts visible: title,
 * status, audience line with its level, then date, size and origin. `leading` comes before the
 * title (the tree's type mark), `title` replaces the plain title (search highlights), `after`
 * follows the facts (the search path).
 */
export function FileRowBody({
  document: d,
  facts,
  ocrMaxPages,
  leading,
  title,
  titleId,
  detailsId,
  after,
  columns,
  density = "full",
}: {
  document: ProjectDocument;
  facts: FileRowFacts;
  ocrMaxPages: number | null;
  leading?: ReactNode;
  title?: ReactNode;
  /** The title's ID (the row's accessible name) and the facts' (its description). */
  titleId?: string;
  detailsId?: string;
  after?: ReactNode;
  /** Details as columns (the project home's Files tab on a wide screen, design §11). */
  columns?: boolean;
  /**
   * "panel" beside a conversation: title and trust line, the status only while not Ready, no
   * date, size or origin (spec `web-shell` "Slim source rows in the conversation panel").
   */
  density?: "full" | "panel";
}) {
  const slim = density === "panel";
  if (columns) {
    return (
      <span
        data-esi-content="files"
        className="grid min-w-0 flex-1 grid-cols-[minmax(0,2.2fr)_minmax(0,1.1fr)_minmax(0,1.6fr)_4.5rem_auto] items-start gap-x-4 text-sm"
      >
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex min-w-0 items-start font-medium text-fg">
            {leading}
            <span id={titleId} className="min-w-0 truncate" title={d.title}>
              {title ?? d.title}
            </span>
          </span>
          <span className="text-xs text-fg-muted tabular-nums">
            {[formatDate(d.createdAt), originWord(d)].filter(Boolean).join(" · ")}
          </span>
          <GeneratedFacts document={d} />
          {after}
        </span>
        <span id={detailsId} className="contents">
          <Status document={d} ocrMaxPages={ocrMaxPages} />
          <AudienceLine
            restricted={facts.audience.restricted}
            text={`${facts.audience.text} · ${facts.classification}`}
          />
          <span className="text-right whitespace-nowrap text-fg-secondary tabular-nums">
            {formatBytes(d.sizeBytes)}
          </span>
        </span>
        <span className="-mt-1 flex justify-end">{facts.actions}</span>
      </span>
    );
  }
  return (
    <span
      data-esi-content="files"
      className="group/row relative flex min-w-0 flex-1 flex-col gap-1 text-sm"
    >
      <span className="flex min-w-0 items-start pr-9 font-medium break-words text-fg">
        {leading}
        <span id={titleId} className="min-w-0">
          {title ?? d.title}
        </span>
      </span>
      <span id={detailsId} className="flex flex-col gap-1">
        {slim && d.status === "ready" ? null : <Status document={d} ocrMaxPages={ocrMaxPages} />}
        <AudienceLine
          restricted={facts.audience.restricted}
          text={`${facts.audience.text} · ${facts.classification}`}
        />
        <GeneratedFacts document={d} />
        {slim ? null : (
          <span className="text-xs text-fg-muted tabular-nums">
            {[formatDate(d.createdAt), formatBytes(d.sizeBytes), originWord(d)]
              .filter(Boolean)
              .join(" · ")}
          </span>
        )}
        {after}
      </span>
      {/* Pointer devices reveal the actions on hover or focus; touch shows them in flow. */}
      <span className="-ml-2 transition-opacity duration-(--duration-fast) [@media(hover:hover)]:absolute [@media(hover:hover)]:-top-1 [@media(hover:hover)]:right-0 [@media(hover:hover)]:ml-0 [@media(hover:hover)]:rounded-md [@media(hover:hover)]:bg-surface-raised [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:shadow-xs [@media(hover:hover)]:group-hover/item:opacity-100 [@media(hover:hover)]:group-focus-within/item:opacity-100 [@media(hover:hover)]:group-data-[menu=open]/item:opacity-100">
        {facts.actions}
      </span>
    </span>
  );
}

export function Status({
  document: d,
  ocrMaxPages,
}: {
  document: ProjectDocument;
  ocrMaxPages: number | null;
}) {
  const { t } = useTranslation();
  if (isPaused(d)) {
    // Paused by the spend limit: neutral, not an error; it resumes on its own.
    return (
      <span className="flex flex-col gap-0.5 text-xs">
        <span className="inline-flex items-center gap-1.5 font-medium text-fg-secondary">
          <span aria-hidden="true" className="size-1.5 rounded-full bg-line-strong" />
          {t("files.paused")}
        </span>
        <span className="text-fg-secondary">{t("files.pausedReason")}</span>
      </span>
    );
  }
  const style = STATUS_STYLE[d.status];
  return (
    <span className="flex flex-col gap-0.5 text-xs">
      <span className={cn("inline-flex items-center gap-1.5 font-medium", style.text)}>
        <span aria-hidden="true" className={cn("size-1.5 rounded-full", style.dot)} />
        {t(`files.statusLabel.${d.status}`)}
      </span>
      {d.status === "failed" ? (
        <span className="text-danger-fg">
          {d.failureReason ? failureText(d.failureReason, ocrMaxPages) : t("files.noReason")}
        </span>
      ) : null}
    </span>
  );
}

/**
 * A row's actions: the direct removal of an incomplete upload, the cite-only line, and a menu
 * with only what the viewer may do (open, download, edit access, retry, delete).
 */
function Actions(props: {
  projectId: string;
  document: ProjectDocument;
  actions: readonly FileAction[];
  retrying: boolean;
  removing: boolean;
  onRetry: () => void;
  onRemove: () => void;
  onEditAccess: (from: HTMLElement | null) => void;
  onDelete: (from: HTMLElement | null) => void;
  menu?: RowMenu;
}) {
  const { projectId, document: d, actions, menu: tree } = props;
  const { t } = useTranslation();
  const has = (action: FileAction) => actions.includes(action);
  // In a tree the menu also holds the removal (the row is the tab stop, not its buttons).
  const menu =
    actions.some((a) => a !== "remove") || (tree !== undefined && (has("remove") || tree.extra));
  const trigger = useRef<HTMLButtonElement>(null);
  const view = useSourceViewer();
  const tab = tree ? -1 : undefined;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {has("remove") ? (
        <Button
          variant="ghost"
          size="xs"
          tabIndex={tab}
          disabled={props.removing}
          onClick={props.onRemove}
        >
          <Trash2 aria-hidden="true" />
          {props.removing ? t("files.removing") : t("files.remove")}
          <span className="sr-only"> {d.title}</span>
        </Button>
      ) : null}
      {!has("download") && d.status !== "uploading" ? (
        <span className="px-2 text-xs text-fg-muted">{t("files.citeOnly")}</span>
      ) : null}
      {menu ? (
        <DropdownMenu open={tree?.open} onOpenChange={tree?.onOpenChange}>
          <DropdownMenuTrigger asChild>
            <Button
              ref={trigger}
              tabIndex={tab}
              variant="ghost"
              size="icon-sm"
              aria-label={t("files.actionsFor", { title: d.title })}
              className="text-fg-muted hover:text-fg"
            >
              <Ellipsis aria-hidden="true" strokeWidth={1.75} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-44"
            onCloseAutoFocus={(event) => {
              const row = tree?.returnFocus();
              if (row) {
                event.preventDefault();
                row.focus();
              }
            }}
          >
            {has("open") && openModeOf(d.mediaType) === "tab" ? (
              <DropdownMenuItem asChild>
                <a href={openHref(projectId, d.id)} target="_blank" rel="noopener noreferrer">
                  <ExternalLink aria-hidden="true" strokeWidth={1.75} />
                  {t("files.open")}
                  <span className="sr-only"> {t("app.newTab")}</span>
                </a>
              </DropdownMenuItem>
            ) : has("open") ? (
              <DropdownMenuItem
                // After the menu releases focus; on close, focus goes back to its row or button.
                onSelect={() =>
                  setTimeout(() =>
                    view({
                      kind: "document",
                      projectId,
                      documentId: d.id,
                      title: d.title,
                      returnFocus: () => tree?.returnFocus() ?? trigger.current,
                    }),
                  )
                }
              >
                <BookOpenText aria-hidden="true" strokeWidth={1.75} />
                {t("files.open")}
              </DropdownMenuItem>
            ) : null}
            {has("download") ? (
              <DropdownMenuItem asChild>
                <a href={downloadHref(projectId, d.id)} target="_blank" rel="noopener noreferrer">
                  <Download aria-hidden="true" strokeWidth={1.75} />
                  {t("files.download")}
                  <span className="sr-only"> {t("app.newTab")}</span>
                </a>
              </DropdownMenuItem>
            ) : null}
            {has("editAccess") ? (
              <DropdownMenuItem onSelect={() => props.onEditAccess(trigger.current)}>
                <KeyRound aria-hidden="true" strokeWidth={1.75} />
                {t("files.editAccess")}
              </DropdownMenuItem>
            ) : null}
            {has("retry") ? (
              <DropdownMenuItem disabled={props.retrying} onSelect={props.onRetry}>
                <RotateCw aria-hidden="true" strokeWidth={1.75} />
                {props.retrying ? t("files.retrying") : t("files.retry")}
              </DropdownMenuItem>
            ) : null}
            {tree?.extra}
            {tree && has("remove") ? (
              <DropdownMenuItem disabled={props.removing} onSelect={props.onRemove}>
                <Trash2 aria-hidden="true" strokeWidth={1.75} />
                {props.removing ? t("files.removing") : t("files.remove")}
              </DropdownMenuItem>
            ) : null}
            {has("delete") ? (
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => props.onDelete(trigger.current)}
              >
                <Trash2 aria-hidden="true" strokeWidth={1.75} />
                {t("files.delete")}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </span>
  );
}
